import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { resolveImageUrl } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import Select from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ImagePlus, Trash2, MapPin, Home, Tag, User, X, ArrowLeft, ArrowRight, Utensils, Bed, Plus } from 'lucide-react';
import { createProperty, updateProperty, getProperties, getUserBuildings } from '../../lib/apiService';
import { API_BASE } from '../../lib/apiConfig';
import Compressor from 'compressorjs';

const INDIAN_STATES = [
  'Andaman and Nicobar Islands','Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chandigarh',
  'Chhattisgarh','Dadra and Nagar Haveli & Daman and Diu','Delhi','Goa','Gujarat','Haryana',
  'Himachal Pradesh','Jammu and Kashmir','Jharkhand','Karnataka','Kerala','Ladakh','Lakshadweep',
  'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Puducherry',
  'Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand',
  'West Bengal'
];

const LISTING_TYPES = [
  { value: 'sell', label: 'For Sale' },
  { value: 'rent', label: 'For Rent' },
  { value: 'pg', label: 'For PG' },
];

const PROPERTY_TYPES = {
  sell: [
    { value: 'Plot', label: 'Plot' },
    { value: 'House', label: 'House' },
    { value: 'Flat', label: 'Flat' },
    { value: 'Building', label: 'Building' },
  ],
  rent: [
    { value: 'Flat', label: 'Flat' },
    { value: 'Appartment', label: 'Appartment' },
    { value: 'House', label: 'House' },
  ],
  pg: [
    { value: 'PG', label: 'PG' }
  ]
};

const FACING_OPTIONS = ['North','South','East','West','North-East','North-West','South-East','South-West'];
const FURNISHING_OPTIONS = ['Unfurnished','Semi-Furnished','Fully Furnished'];
const BHK_OPTIONS = ['1RK','2RK','3RK','4RK','5RK','5+RK','1BHK','2BHK','3BHK','4BHK','5BHK','5+BHK'];
const PRICE_TYPES = ['Fixed','Negotiable'];
const USAGE_TYPES = ['Residential','Commercial','Agricultural'];
const ROOM_TYPES = ['Single', 'Sharing'];

const initialForm = {
  listing_type: 'sell',
  property_type: '',
  property_title: '',
  building_name: '',
  description: '',
  address: '',
  landmark: '',
  city: '',
  district: '',
  state: '',
  latitude: '',
  longitude: '',
  price: '',
  price_type: 'Fixed',
  security_deposit: '',
  area_sqft: '',
  facing: '',
  furnishing: '',
  bedrooms: '',
  bathrooms: '',
  bhk: '',
  floors: '',
  flats: '',
  usage_type: 'Residential',
  amenities: '',
  owner_name: '',
  owner_phone: '',
  alt_phone: '',
  owner_email: '',
  yt_link: '',
  status: 'active',
  rooms: [{ roomType: 'Single', attachedWashroom: false, ac: false, numBeds: '1', pricePerBed: '' }],
  serves_food: 'no',
};

const SelectField = ({ label, value, onChange, options, required, placeholder = 'Select...' }) => {
  const selectOptions = options.map(opt =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );
  return (
    <div>
      <Label className="text-sm font-medium text-slate-700">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <div className="mt-1.5">
        <Select
          options={selectOptions}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
        />
      </div>
    </div>
  );
};

const AddPropertyPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(propertyId);

  const [form, setForm] = useState(initialForm);
  const [photos, setPhotos] = useState([null, null, null, null, null]);
  const [activeSlot, setActiveSlot] = useState(null);
  
  const [foodMenuPhotos, setFoodMenuPhotos] = useState([null, null, null, null, null]);
  const [activeFoodMenuSlot, setActiveFoodMenuSlot] = useState(null);

  const [districts, setDistricts] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [buildingOptions, setBuildingOptions] = useState([]);
  const [manualBuildingEntry, setManualBuildingEntry] = useState(false);

  const isRent = form.listing_type === 'rent';
  const isPG = form.listing_type === 'pg';
  const showBedsBathsBhk = (isRent || form.listing_type === 'sell') && ['House', 'Flat', 'Appartment'].includes(form.property_type);
  const showBuildingFields = form.listing_type === 'sell' && form.property_type === 'Building';
  const showFacingField = form.listing_type === 'sell';
  const showSecurityDeposit = isRent || isPG;
  const showRoomsField = isPG;

  useEffect(() => {
    getUserBuildings()
      .then(res => {
        if (res?.status && Array.isArray(res.data)) {
          setBuildingOptions(res.data.map(name => ({ label: name, value: name })));
        }
      })
      .catch(() => {});
  }, []);

  const fetchDistricts = useCallback(async (state) => {
    if (!state) { setDistricts([]); return; }
    setLoadingDistricts(true);
    try {
      const res = await fetch(`${API_BASE}/areas/list.php?state=${encodeURIComponent(state)}`);
      const data = await res.json();
      setDistricts(data.areas || []);
    } catch {
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  }, []);

  useEffect(() => {
    if (form.state) fetchDistricts(form.state);
  }, [form.state, fetchDistricts]);

  useEffect(() => {
    if (isEditMode) {
      getProperties({ filters: { id: propertyId } })
        .then(res => {
          if (res.properties && res.properties.length > 0) {
            const p = res.properties[0];
            setForm({
              listing_type: p.listing_type || 'sell',
              property_type: p.property_type || '',
              property_title: p.property_title || '',
              building_name: p.building_name || '',
              description: p.description || '',
              address: p.address || '',
              landmark: p.landmark || '',
              city: p.city || '',
              district: p.district || '',
              state: p.state || '',
              latitude: p.latitude || '',
              longitude: p.longitude || '',
              price: p.price || '',
              price_type: p.price_type || 'Fixed',
              security_deposit: p.security_deposit || '',
              area_sqft: p.area_sqft || '',
              facing: p.facing || '',
              furnishing: p.furnishing || '',
              bedrooms: p.bedrooms || '',
              bathrooms: p.bathrooms || '',
              bhk: p.bhk || '',
              floors: p.floors || '',
              flats: p.total_flats || '',
              usage_type: p.usage_type || 'Residential',
              amenities: p.amenities || '',
              owner_name: p.owner_name || '',
              owner_phone: p.owner_phone || '',
              alt_phone: p.alt_phone || '',
              owner_email: p.owner_email || '',
              yt_link: p.yt_link || '',
              status: p.status || 'active',
              rooms: (typeof p.rooms === 'string') ? JSON.parse(p.rooms) : (Array.isArray(p.rooms) && p.rooms.length > 0 ? p.rooms : [{ roomType: 'Single', attachedWashroom: false, ac: false, numBeds: '1', pricePerBed: '' }]),
              serves_food: p.serves_food === 1 ? 'yes' : 'no',
            });

            // Populate existing images
            const loadedPhotos = [null, null, null, null, null];
            if (Array.isArray(p.images)) {
              p.images.forEach((url, i) => {
                if (i < 5) {
                  const filename = url.substring(url.lastIndexOf('/') + 1);
                  loadedPhotos[i] = { type: 'existing', url: resolveImageUrl(url, 'property_images'), filename };
                }
              });
            }
            setPhotos(loadedPhotos);

            // Populate existing food menu images
            const loadedFoodPhotos = [null, null, null, null, null];
            if (Array.isArray(p.food_menu_images)) {
              p.food_menu_images.forEach((url, i) => {
                if (i < 5) {
                  const filename = url.substring(url.lastIndexOf('/') + 1);
                  loadedFoodPhotos[i] = { type: 'existing', url: resolveImageUrl(url, 'food_menu'), filename };
                }
              });
            }
            setFoodMenuPhotos(loadedFoodPhotos);

          } else {
            toast.error('Property not found');
            navigate('/properties');
          }
        })
        .catch(err => {
          toast.error('Failed to load property details');
        });
    }
  }, [isEditMode, propertyId, navigate]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleListingTypeChange = (type) => {
    setForm(prev => ({ ...prev, listing_type: type, property_type: PROPERTY_TYPES[type][0].value }));
  };

  const handleImageUpload = async (e, stateSetter, activeSlotState, setActiveSlotState) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const compressImage = (file) => {
      return new Promise((resolve) => {
        new Compressor(file, {
          quality: 0.6,
          success(result) { resolve(result); },
          error(err) { resolve(file); } // fallback to original file if compression fails
        });
      });
    };

    const compressedFiles = [];
    for (const file of files) {
      compressedFiles.push(await compressImage(file));
    }

    stateSetter(prev => {
      const next = [...prev];
      let fileIdx = 0;
      let targetIdx = activeSlotState !== null ? activeSlotState : next.indexOf(null);

      while (targetIdx !== -1 && fileIdx < compressedFiles.length) {
        if (next[targetIdx] === null) {
          const file = compressedFiles[fileIdx];
          next[targetIdx] = {
            type: 'new',
            file,
            preview: URL.createObjectURL(file)
          };
          fileIdx++;
        }
        targetIdx = next.indexOf(null);
      }
      return next;
    });
    setActiveSlotState(null);
    e.target.value = '';
  };

  const removeImage = (idx, stateSetter) => {
    stateSetter(prev => {
      const next = [...prev];
      const item = next[idx];
      if (item && item.type === 'new') {
        URL.revokeObjectURL(item.preview);
      }
      next[idx] = null;
      return next;
    });
  };

  const movePhoto = (fromIdx, direction, stateSetter) => {
    const toIdx = fromIdx + direction;
    if (toIdx < 0 || toIdx >= 5) return;
    stateSetter(prev => {
      const next = [...prev];
      const temp = next[fromIdx];
      next[fromIdx] = next[toIdx];
      next[toIdx] = temp;
      return next;
    });
  };

  const addRoom = () => {
    setForm(prev => ({
      ...prev,
      rooms: [...prev.rooms, { roomType: 'Single', attachedWashroom: false, ac: false, numBeds: '1', pricePerBed: '' }]
    }));
  };

  const removeRoom = (idx) => {
    setForm(prev => ({
      ...prev,
      rooms: prev.rooms.filter((_, i) => i !== idx)
    }));
  };

  const handleRoomChange = (idx, field, value) => {
    setForm(prev => {
      const newRooms = [...prev.rooms];
      newRooms[idx] = { ...newRooms[idx], [field]: value };
      return { ...prev, rooms: newRooms };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const activePhotos = photos.filter(p => p !== null);
    if (activePhotos.length < 1) { toast.error('Add at least 1 property image'); return; }
    if (!form.property_title.trim()) { toast.error('Property title is required'); return; }
    if (!form.property_type) { toast.error('Select property type'); return; }
    if (['Flat', 'Building', 'Appartment'].includes(form.property_type)) {
      if (!form.building_name || !form.building_name.trim()) {
        toast.error('Building / Society Name is required');
        return;
      }
    }
    if (!form.address.trim()) { toast.error('Address cannot be empty'); return; }
    if (!form.landmark || !form.landmark.trim()) { toast.error('Landmark cannot be empty'); return; }
    if (!form.city.trim()) { toast.error('City cannot be empty'); return; }
    if (!form.district) { toast.error('District cannot be empty'); return; }
    if (!form.state) { toast.error('State cannot be empty'); return; }
    
    if (!isPG) {
      if (!form.usage_type) { toast.error('Select usage type'); return; }
      if (!form.area_sqft || Number(form.area_sqft) < 1) { toast.error('Enter valid Area (sq.ft)'); return; }
      if (!form.price || Number(form.price) < 1) { toast.error('Enter valid Price'); return; }
      if (!form.price_type) { toast.error('Please select Price Type'); return; }
    }

    if (!form.latitude || !form.longitude) { toast.error('Latitude and Longitude are required'); return; }

    if (showBedsBathsBhk) {
      if (!form.bedrooms || Number(form.bedrooms) < 0) { toast.error('Enter valid Bedrooms'); return; }
      if (!form.bathrooms || Number(form.bathrooms) < 0) { toast.error('Enter valid Bathrooms'); return; }
      if (!form.bhk) { toast.error('Please select BHK'); return; }
    }

    if (showBuildingFields) {
      if (!form.floors || Number(form.floors) < 0) { toast.error('Enter valid Floors count'); return; }
      if (!form.flats || Number(form.flats) < 0) { toast.error('Enter valid Flats count'); return; }
    }

    if (showFacingField) {
      if (!form.facing) { toast.error('Please select Facing direction'); return; }
    }

    if (isPG) {
      if (!form.rooms || form.rooms.length === 0) { toast.error('Add at least one room'); return; }
      for (let i = 0; i < form.rooms.length; i++) {
        const room = form.rooms[i];
        if (!room.roomType) { toast.error(`Select Room Type for Room ${i + 1}`); return; }
        if (!room.numBeds || Number(room.numBeds) < 1) { toast.error(`Enter valid number of beds for Room ${i + 1}`); return; }
        if (!room.pricePerBed || Number(room.pricePerBed) < 1) { toast.error(`Enter valid price per bed for Room ${i + 1}`); return; }
      }
    }

    if (!form.owner_name.trim()) { toast.error('Owner name is required'); return; }
    if (!form.owner_phone || !/^\d{10}$/.test(form.owner_phone)) { toast.error('Enter valid 10-digit phone'); return; }
    if (form.alt_phone && !/^\d{10}$/.test(form.alt_phone)) { toast.error('Enter valid 10-digit alt phone'); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { 
        if (v !== '' && v !== null && k !== 'rooms') fd.append(k, v); 
      });

      // Special handling for PG fields and general user overriding
      fd.append('user_id', 2); // Admin panels entries inject hello@joldipabo.com (id: 2)

      if (isPG) {
        fd.append('rooms', JSON.stringify(form.rooms));
        fd.append('serves_food', form.serves_food === 'yes' ? 1 : 0);
      }

      if (isEditMode) {
        fd.append('property_id', propertyId);
      }

      const imagesOrder = [];
      let newFileCounter = 0;

      activePhotos.forEach((photo) => {
        if (photo.type === 'existing') {
          imagesOrder.push(photo.filename);
        } else if (photo.type === 'new') {
          const fileKey = `photo_${newFileCounter}`;
          fd.append(fileKey, photo.file, photo.file.name);
          imagesOrder.push(fileKey);
          newFileCounter++;
        }
      });
      fd.append('images_order', JSON.stringify(imagesOrder));

      if (isPG && form.serves_food === 'yes') {
        const activeFoodPhotos = foodMenuPhotos.filter(p => p !== null);
        const foodImagesOrder = [];
        let newFoodFileCounter = 0;
        
        activeFoodPhotos.forEach((photo) => {
          if (photo.type === 'existing') {
            foodImagesOrder.push(photo.filename);
          } else if (photo.type === 'new') {
            const fileKey = `food_photo_${newFoodFileCounter}`;
            fd.append(fileKey, photo.file, photo.file.name);
            foodImagesOrder.push(fileKey);
            newFoodFileCounter++;
          }
        });
        fd.append('food_menu_images_order', JSON.stringify(foodImagesOrder));
      }

      if (isEditMode) {
        await updateProperty(propertyId, fd);
        toast.success('Property updated successfully!');
        navigate('/properties');
      } else {
        await createProperty(fd);
        toast.success('Property listed successfully!');
        setForm(initialForm);
        setManualBuildingEntry(false);
        photos.forEach(p => { if (p && p.type === 'new') URL.revokeObjectURL(p.preview); });
        setPhotos([null, null, null, null, null]);
        foodMenuPhotos.forEach(p => { if (p && p.type === 'new') URL.revokeObjectURL(p.preview); });
        setFoodMenuPhotos([null, null, null, null, null]);
        setDistricts([]);
      }
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    if (isEditMode) {
      navigate('/properties');
    } else {
      setForm(initialForm);
      setManualBuildingEntry(false);
      photos.forEach(p => { if (p && p.type === 'new') URL.revokeObjectURL(p.preview); });
      setPhotos([null, null, null, null, null]);
      foodMenuPhotos.forEach(p => { if (p && p.type === 'new') URL.revokeObjectURL(p.preview); });
      setFoodMenuPhotos([null, null, null, null, null]);
    }
  };

  const renderImageUploader = (title, subtitle, photoState, stateSetter, activeSlotState, setActiveSlotState, inputId) => (
    <Card className="p-6">
      <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
        <ImagePlus className="w-4 h-4" /> {title} <span className="text-red-500">*</span>
      </h2>
      <p className="text-xs text-slate-500 mb-3">{subtitle}</p>
      <div className="grid grid-cols-5 gap-3">
        {Array.from({ length: 5 }, (_, i) => {
          const img = photoState[i];
          return (
            <div
              key={i}
              className={`relative aspect-square rounded-xl border-2 flex items-center justify-center overflow-hidden cursor-pointer transition-all ${
                img ? 'border-orange-300' : 'border-dashed border-slate-300 hover:border-orange-300 bg-slate-50'
              }`}
              onClick={() => {
                if (!img) {
                  setActiveSlotState(i);
                  document.getElementById(inputId).click();
                }
              }}
            >
              {img ? (
                <>
                  <img src={img.type === 'existing' ? img.url : img.preview} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  <button type="button" onClick={e => { e.stopPropagation(); removeImage(i, stateSetter); }} className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow z-10 hover:bg-red-600">
                    <X className="w-3 h-3 text-white" />
                  </button>
                  <div className="absolute bottom-1 left-1 right-1 flex justify-between gap-1 z-10">
                    {i > 0 && <button type="button" onClick={e => { e.stopPropagation(); movePhoto(i, -1, stateSetter); }} className="w-6 h-6 bg-black/60 rounded-full flex items-center justify-center hover:bg-slate-900"><ArrowLeft className="w-3 h-3 text-white" /></button>}
                    {i < 4 && <button type="button" onClick={e => { e.stopPropagation(); movePhoto(i, 1, stateSetter); }} className="w-6 h-6 bg-black/60 rounded-full flex items-center justify-center hover:bg-slate-900 ml-auto"><ArrowRight className="w-3 h-3 text-white" /></button>}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-1 text-slate-400">
                  <ImagePlus className="w-6 h-6" />
                  <span className="text-[10px] font-medium">Add</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <input id={inputId} type="file" accept="image/*" multiple onChange={e => handleImageUpload(e, stateSetter, activeSlotState, setActiveSlotState)} className="hidden" />
    </Card>
  );

  return (
    <div className="px-6 py-8 max-w-5xl mx-auto pb-24">
      <Helmet>
        <title>Add Property | JoldiPabo Data Entry</title>
      </Helmet>
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isEditMode ? 'Edit Property Listing' : 'Add Property Listing'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isEditMode ? 'Modify property listing information, images, and sequence.' : 'List a property for sale, rent, or PG with complete details.'}
          </p>
        </div>
        <div className="w-48">
          <Select 
            options={LISTING_TYPES}
            value={form.listing_type}
            onChange={handleListingTypeChange}
            isSearchable={false}
            isClearable={false}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Images */}
        {renderImageUploader('Property Images', 'Add at least 1 image, up to 5 maximum.', photos, setPhotos, activeSlot, setActiveSlot, 'img-upload')}

        {/* Basic Details */}
        <Card className="p-6">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Home className="w-4 h-4" /> Basic Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Property Title <span className="text-red-500">*</span></Label>
              <Input className="mt-1.5 h-11" placeholder="e.g. 3BHK Flat in Kohima City Centre" value={form.property_title} onChange={e => handleChange('property_title', e.target.value)} required />
            </div>
            {!isPG && (
              <>
                <SelectField label="Property Type" required value={form.property_type} onChange={v => handleChange('property_type', v)} options={PROPERTY_TYPES[form.listing_type] || PROPERTY_TYPES.sell} />
                <SelectField label="Usage Type" value={form.usage_type} onChange={v => handleChange('usage_type', v)} options={isRent ? ['Residential', 'Commercial'] : (form.property_type === 'Plot' ? ['Residential', 'Commercial', 'Agricultural'] : ['Residential', 'Commercial'])} required />
              </>
            )}

            {['Flat', 'Building', 'Appartment'].includes(form.property_type) && (
              <div className="md:col-span-2">
                <Label className="text-sm font-medium text-slate-700">
                  Building / Society Name <span className="text-red-500">*</span>
                </Label>
                {buildingOptions.length > 0 && !manualBuildingEntry ? (
                  <div className="mt-1.5">
                    <Select
                      options={[
                        ...(form.building_name && !buildingOptions.some(o => o.value === form.building_name)
                          ? [{ label: form.building_name, value: form.building_name }]
                          : []),
                        ...buildingOptions,
                        { label: '+ Enter New Manually', value: '__new__' }
                      ]}
                      value={form.building_name}
                      onChange={v => {
                        if (v === '__new__') {
                          setManualBuildingEntry(true);
                          handleChange('building_name', '');
                        } else {
                          handleChange('building_name', v);
                        }
                      }}
                      placeholder="Select Building / Society"
                    />
                  </div>
                ) : (
                  <div className="mt-1.5">
                    <Input
                      className="h-11"
                      placeholder="Enter Building / Society Name"
                      value={form.building_name}
                      onChange={e => handleChange('building_name', e.target.value)}
                      required
                    />
                    {buildingOptions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => { setManualBuildingEntry(false); handleChange('building_name', ''); }}
                        className="mt-2 text-xs font-semibold text-[#ee7923] hover:underline flex items-center gap-1"
                      >
                        ← Select from past entries
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {form.property_type === 'House' && (
              <div className="md:col-span-2">
                <Label className="text-sm font-medium text-slate-700">House Name / Number</Label>
                <Input
                  className="mt-1.5 h-11"
                  placeholder="e.g. Sunrise Villa"
                  value={form.building_name}
                  onChange={e => handleChange('building_name', e.target.value)}
                />
              </div>
            )}

            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea className="mt-1.5 min-h-[80px]" placeholder="Describe the property..." value={form.description} onChange={e => handleChange('description', e.target.value)} />
            </div>
          </div>
        </Card>

        {/* Location */}
        <Card className="p-6">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Location
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div className="md:col-span-2 xl:col-span-3">
              <Label>Full Address <span className="text-red-500">*</span></Label>
              <Input className="mt-1.5 h-11" placeholder="Full address" value={form.address} onChange={e => handleChange('address', e.target.value)} required />
            </div>
            <div>
              <Label>Landmark <span className="text-red-500">*</span></Label>
              <Input className="mt-1.5 h-11" placeholder="Nearby landmark" value={form.landmark} onChange={e => handleChange('landmark', e.target.value)} required />
            </div>
            <div>
              <Label>City <span className="text-red-500">*</span></Label>
              <Input className="mt-1.5 h-11" placeholder="City" value={form.city} onChange={e => handleChange('city', e.target.value)} required />
            </div>
            <SelectField label="State" required value={form.state} onChange={v => { handleChange('state', v); handleChange('district', ''); }} options={INDIAN_STATES} />
            <div>
              <Label>District <span className="text-red-500">*</span></Label>
              <div className="mt-1.5">
                <Select options={districts.map(d => ({ value: d.district, label: d.district }))} value={form.district} onChange={v => handleChange('district', v)} placeholder={loadingDistricts ? 'Loading...' : 'Select district'} isDisabled={loadingDistricts || !form.state} />
              </div>
            </div>
            <div>
              <Label>Latitude <span className="text-red-500">*</span></Label>
              <Input className="mt-1.5 h-11" placeholder="e.g. 25.6741" value={form.latitude} onChange={e => handleChange('latitude', e.target.value)} required />
            </div>
            <div>
              <Label>Longitude <span className="text-red-500">*</span></Label>
              <Input className="mt-1.5 h-11" placeholder="e.g. 94.1079" value={form.longitude} onChange={e => handleChange('longitude', e.target.value)} required />
            </div>
          </div>
        </Card>

        {/* Pricing */}
        {!isPG && (
          <Card className="p-6">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">💰 Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Price (₹) <span className="text-red-500">*</span></Label>
                <Input type="number" min="0" className="mt-1.5 h-11" placeholder="e.g. 2500000" value={form.price} onChange={e => handleChange('price', e.target.value)} required={!isPG} />
              </div>
              <SelectField label="Price Type" value={form.price_type} onChange={v => handleChange('price_type', v)} options={PRICE_TYPES} required />
              {showSecurityDeposit && (
                <div>
                  <Label>Security Deposit (₹)</Label>
                  <Input type="number" min="0" className="mt-1.5 h-11" placeholder="e.g. 30000" value={form.security_deposit} onChange={e => handleChange('security_deposit', e.target.value)} />
                </div>
              )}
              <div>
                <Label>Area (sq.ft) <span className="text-red-500">*</span></Label>
                <Input type="number" min="1" className="mt-1.5 h-11" placeholder="e.g. 1200" value={form.area_sqft} onChange={e => handleChange('area_sqft', e.target.value)} required />
              </div>
            </div>
          </Card>
        )}

        {/* Property Details (Sale/Rent only) */}
        {!isPG && (
          <Card className="p-6">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">🏠 Property Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {showFacingField && (
                <SelectField label="Facing" required value={form.facing} onChange={v => handleChange('facing', v)} options={FACING_OPTIONS} />
              )}
              <SelectField label="Furnishing" required value={form.furnishing} onChange={v => handleChange('furnishing', v)} options={FURNISHING_OPTIONS} />
              {showBedsBathsBhk && (
                <>
                  <div>
                    <Label>Bedrooms <span className="text-red-500">*</span></Label>
                    <Input type="number" min="0" className="mt-1.5 h-11" placeholder="e.g. 3" value={form.bedrooms} onChange={e => handleChange('bedrooms', e.target.value)} required />
                  </div>
                  <div>
                    <Label>Bathrooms <span className="text-red-500">*</span></Label>
                    <Input type="number" min="0" className="mt-1.5 h-11" placeholder="e.g. 2" value={form.bathrooms} onChange={e => handleChange('bathrooms', e.target.value)} required />
                  </div>
                  <SelectField label="BHK" required value={form.bhk} onChange={v => handleChange('bhk', v)} options={BHK_OPTIONS} />
                </>
              )}
              {showBuildingFields && (
                <>
                  <div>
                    <Label>Total Floors <span className="text-red-500">*</span></Label>
                    <Input type="number" min="0" className="mt-1.5 h-11" placeholder="e.g. 5" value={form.floors} onChange={e => handleChange('floors', e.target.value)} required />
                  </div>
                  <div>
                    <Label>Total Flats <span className="text-red-500">*</span></Label>
                    <Input type="number" min="0" className="mt-1.5 h-11" placeholder="e.g. 20" value={form.flats} onChange={e => handleChange('flats', e.target.value)} required />
                  </div>
                </>
              )}
              <div className="md:col-span-3">
                <Label>Amenities</Label>
                <Input className="mt-1.5 h-11" placeholder="e.g. CCTV, Parking, Lift, WiFi" value={form.amenities} onChange={e => handleChange('amenities', e.target.value)} />
              </div>
              <div>
                <Label>YouTube Link</Label>
                <Input className="mt-1.5 h-11" placeholder="https://youtube.com/..." value={form.yt_link} onChange={e => handleChange('yt_link', e.target.value)} />
              </div>
            </div>
          </Card>
        )}

        {/* PG Specific Details */}
        {isPG && (
          <Card className="p-6">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Bed className="w-4 h-4" /> PG Configuration
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label>Security Deposit (₹)</Label>
                <Input type="number" min="0" className="mt-1.5 h-11" placeholder="e.g. 5000" value={form.security_deposit} onChange={e => handleChange('security_deposit', e.target.value)} />
              </div>
              <div>
                <Label>Amenities</Label>
                <Input className="mt-1.5 h-11" placeholder="e.g. WiFi, Washing Machine, Power Backup" value={form.amenities} onChange={e => handleChange('amenities', e.target.value)} />
              </div>
            </div>

            <h3 className="text-sm font-medium text-slate-700 mb-3 border-b pb-2">Room Configurations</h3>
            <div className="space-y-4 mb-6">
              {form.rooms.map((room, idx) => (
                <div key={idx} className="p-4 border rounded-lg bg-slate-50 flex gap-4 flex-wrap relative">
                  {idx > 0 && (
                    <button type="button" onClick={() => removeRoom(idx)} className="absolute -top-3 -right-3 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                  <div className="w-full md:flex-1 min-w-[150px]">
                    <SelectField label="Room Type" value={room.roomType} onChange={v => handleRoomChange(idx, 'roomType', v)} options={ROOM_TYPES} required />
                  </div>
                  <div className="w-full md:flex-1 min-w-[150px]">
                    <Label className="text-sm font-medium text-slate-700">Number of Beds <span className="text-red-500">*</span></Label>
                    <Input type="number" min="1" className="mt-1.5 h-11" value={room.numBeds} onChange={e => handleRoomChange(idx, 'numBeds', e.target.value)} required />
                  </div>
                  <div className="w-full md:flex-1 min-w-[150px]">
                    <Label className="text-sm font-medium text-slate-700">Price Per Bed (₹) <span className="text-red-500">*</span></Label>
                    <Input type="number" min="1" className="mt-1.5 h-11" placeholder="e.g. 3000" value={room.pricePerBed} onChange={e => handleRoomChange(idx, 'pricePerBed', e.target.value)} required />
                  </div>
                  <div className="flex items-center gap-4 mt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={room.ac} onChange={e => handleRoomChange(idx, 'ac', e.target.checked)} className="rounded text-[#ee7923]" />
                      <span className="text-sm font-medium text-slate-700">AC</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={room.attachedWashroom} onChange={e => handleRoomChange(idx, 'attachedWashroom', e.target.checked)} className="rounded text-[#ee7923]" />
                      <span className="text-sm font-medium text-slate-700">Attached Washroom</span>
                    </label>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addRoom} className="w-full border-dashed border-2">
                <Plus className="w-4 h-4 mr-2" /> Add Another Room Type
              </Button>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-sm font-medium text-slate-700 mb-4 flex items-center gap-2">
                <Utensils className="w-4 h-4" /> Food & Mess
              </h3>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="serves_food" checked={form.serves_food === 'yes'} onChange={() => handleChange('serves_food', 'yes')} />
                  <span className="text-sm font-medium">Serves Food</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="serves_food" checked={form.serves_food === 'no'} onChange={() => handleChange('serves_food', 'no')} />
                  <span className="text-sm font-medium">Does Not Serve Food</span>
                </label>
              </div>
            </div>
          </Card>
        )}

        {isPG && form.serves_food === 'yes' && renderImageUploader(
          'Food Menu Images', 
          'Upload daily/weekly food menu photos.', 
          foodMenuPhotos, setFoodMenuPhotos, activeFoodMenuSlot, setActiveFoodMenuSlot, 'food-img-upload'
        )}

        {/* Owner Info */}
        <Card className="p-6">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <User className="w-4 h-4" /> Owner / Contact Info
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Owner Name <span className="text-red-500">*</span></Label>
              <Input className="mt-1.5 h-11" placeholder="Full name" value={form.owner_name} onChange={e => handleChange('owner_name', e.target.value)} required />
            </div>
            <div>
              <Label>Phone Number <span className="text-red-500">*</span></Label>
              <Input
                className="mt-1.5 h-11"
                placeholder="10-digit number"
                maxLength={10}
                value={form.owner_phone}
                onChange={e => handleChange('owner_phone', e.target.value.replace(/\D/g, ''))}
                required
              />
            </div>
            <div>
              <Label>Alt Phone <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                className="mt-1.5 h-11"
                placeholder="Alternative number"
                maxLength={10}
                value={form.alt_phone}
                onChange={e => handleChange('alt_phone', e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input className="mt-1.5 h-11" type="email" placeholder="owner@email.com" value={form.owner_email} onChange={e => handleChange('owner_email', e.target.value)} />
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-3 sticky bottom-4 z-50">
          <Button type="button" variant="outline" onClick={handleReset} disabled={submitting} className="bg-white">
            Reset
          </Button>
          <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white px-8 shadow-lg">
            {submitting ? 'Saving...' : 'Save Property'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddPropertyPage;
